/**
 * 预选动作（§6.8 / §11.5）。
 *
 * 价值不在「省一个 RTT」，而在**节奏**：六人桌一圈要等五个人，能提前把「反正我要过牌」
 * 按掉，玩家就不用一直盯着屏幕。代价是必须把「设置时的世界」和「轮到你时的世界」
 * 逐条比对——牌桌在这段时间里会变，盲目执行预选就是替玩家做了他没做的决定。
 *
 * 失效规则（§6.8，逐条实现在 resolvePreAction 里）：
 *   checkOrFold → 永远执行：能过牌就过牌，否则弃牌
 *   check       → canCheck 为假时**清空不执行，绝不降级为弃牌**
 *   call{to}    → callTo 变了就清空（「注额变了」）
 *   callAny     → 恒执行；但注额涨到设置时的 2 倍以上时取消，回到正常计时
 * 生命周期：每条街开始清空、使用后清空、玩家主动展开加注面板时清空。
 */

import type { ReactNode } from 'react'
import type { ActionIntent, Chips, LegalActions, PublicHandState, SeatIndex } from '@poker/shared'

import { formatChips } from './useBetSizing.js'

/** 预选执行前的「反悔窗口」：让玩家先看见牌桌发生了什么 */
export const PRE_ACTION_DELAY_MS = 250
/** 剩余时间不足这个值就跳过反悔窗口直接提交，免得窗口把超时判决吃掉 */
export const PRE_ACTION_SKIP_DELAY_UNDER_MS = 1500

export type PreActionChoice =
  | { readonly kind: 'checkOrFold' }
  | { readonly kind: 'check' }
  /** to 是设置那一刻的 callTo（raise-to 语义），轮到时用它比对注额有没有变 */
  | { readonly kind: 'call'; readonly to: Chips }
  /** betToMatchAtSet 用于「注额翻倍就取消」的护栏 */
  | { readonly kind: 'callAny'; readonly betToMatchAtSet: Chips }

export type PreActionResolution =
  | { readonly ok: true; readonly intent: ActionIntent; readonly label: string }
  /** reason 非 null 时给一条 toast；null 表示静默清空 */
  | { readonly ok: false; readonly reason: string | null }

/**
 * 轮到自己时把预选折算成意图。**纯函数**，是本模块唯一需要严格测试的部分。
 *
 * `betToMatch` 由调用方从 view 取——LegalActions 不带这个字段，而 callAny 的
 * 「翻倍取消」护栏必须拿它比。
 */
export function resolvePreAction(
  choice: PreActionChoice,
  legal: LegalActions,
  betToMatch: Chips,
): PreActionResolution {
  if (!legal.onAction) return { ok: false, reason: null }

  switch (choice.kind) {
    case 'checkOrFold':
      return legal.canCheck
        ? { ok: true, intent: { type: 'check' }, label: '过牌' }
        : { ok: true, intent: { type: 'fold' }, label: '弃牌' }

    case 'check':
      // 绝不降级为弃牌：玩家勾的是「没人下注我就过」，不是「我要弃牌」
      return legal.canCheck
        ? { ok: true, intent: { type: 'check' }, label: '过牌' }
        : { ok: false, reason: '有人下注，已取消预选' }

    case 'call': {
      if (legal.canCheck) return { ok: false, reason: '注额变了，已取消预选' }
      if (legal.callTo !== choice.to) return { ok: false, reason: '注额变了，已取消预选' }
      if (!legal.canCall) return { ok: false, reason: null }
      return { ok: true, intent: { type: 'call' }, label: `跟注 ${formatChips(legal.toCall)}` }
    }

    case 'callAny': {
      // 无人下注时「跟任何注」等价于过牌：引擎对 toCall === 0 的 call 会直接报 NOTHING_TO_CALL
      if (legal.canCheck) return { ok: true, intent: { type: 'check' }, label: '过牌' }
      if (betToMatch > choice.betToMatchAtSet * 2) {
        return { ok: false, reason: '注额涨了一倍以上，已取消预选' }
      }
      if (!legal.canCall) return { ok: false, reason: null }
      return { ok: true, intent: { type: 'call' }, label: `跟注 ${formatChips(legal.toCall)}` }
    }
  }
}

// ============================================================================
// 预估注额：轮到自己**之前**没有 LegalActions，只能从公共视图推
// ============================================================================

export interface PreActionProspect {
  readonly toCall: Chips
  readonly callTo: Chips
  readonly betToMatch: Chips
}

/**
 * 从公共视图算出「如果现在轮到我，要跟多少」。
 * 这只是个提示值——真正提交前一律用 resolvePreAction 拿真实 LegalActions 再校验一次。
 * hero 已弃牌 / 全下 / 不在本手牌里时返回 null（没有可预选的动作）。
 */
export function computeProspect(
  view: PublicHandState | null,
  heroSeat: SeatIndex | null,
): PreActionProspect | null {
  if (view === null || heroSeat === null) return null
  if (view.phase === 'complete') return null
  const me = view.players[heroSeat]
  if (me === undefined || me.status !== 'active' || me.stack <= 0) return null
  const toCall = Math.max(0, view.betToMatch - me.committedThisRound)
  const maxTo = me.committedThisRound + me.stack
  return {
    toCall: Math.min(toCall, me.stack),
    callTo: Math.min(view.betToMatch, maxTo),
    betToMatch: view.betToMatch,
  }
}

/** 两个选择是否等价（用于按钮高亮） */
export function sameChoice(a: PreActionChoice | null, b: PreActionChoice | null): boolean {
  if (a === null || b === null) return a === b
  if (a.kind !== b.kind) return false
  if (a.kind === 'call' && b.kind === 'call') return a.to === b.to
  return true
}

// ============================================================================
// UI
// ============================================================================

export interface PreActionBarProps {
  readonly value: PreActionChoice | null
  readonly onChange: (next: PreActionChoice | null) => void
  readonly prospect: PreActionProspect
  readonly disabled?: boolean
}

interface Option {
  readonly choice: PreActionChoice
  readonly label: string
}

/** 面对下注与无人下注时能选的东西完全不同，混在一起渲染只会让人点错 */
function optionsFor(p: PreActionProspect): Option[] {
  if (p.toCall <= 0) {
    return [
      { choice: { kind: 'check' }, label: '过牌' },
      { choice: { kind: 'checkOrFold' }, label: '过牌 / 弃牌' },
      { choice: { kind: 'callAny', betToMatchAtSet: p.betToMatch }, label: '跟任何注' },
    ]
  }
  return [
    { choice: { kind: 'checkOrFold' }, label: '过牌 / 弃牌' },
    { choice: { kind: 'call', to: p.callTo }, label: `跟注 ${formatChips(p.toCall)}` },
    { choice: { kind: 'callAny', betToMatchAtSet: p.betToMatch }, label: '跟任何注' },
  ]
}

/**
 * 轮到自己之前的预选条。
 *
 * 它住在固定高度 dock 底部的轻量状态行里，不是主按钮行 —— 固定外框保证
 * 等待与行动切换时牌桌不跳。样式上也刻意比主按钮弱一个量级
 * （ghost 描边 + 14px 小字 + 不上实心填充）：它不是「现在要执行的动作」，
 * 视觉权重和主按钮持平会让人误以为轮到自己了。
 *
 * 命中区仍然按 44px 下限给足（UI 规则 CRITICAL）—— 视觉可以弱，手指不能。
 * 选中态用 `--c-turn` 的**描边 + 淡底**而不是整块实心：实心黄在一条细条里太抢，
 * 而这里的目的只是「让你确认自己勾了哪一个」。
 */
export function PreActionBar({
  value,
  onChange,
  prospect,
  disabled = false,
}: PreActionBarProps): ReactNode {
  const options = optionsFor(prospect)
  return (
    <div className="adock__pre" role="group" aria-label="预选动作">
      <span className="adock__pre-title">预选</span>
      {options.map((o) => {
        const active = sameChoice(value, o.choice)
        return (
          <button
            key={o.choice.kind}
            type="button"
            className="adock__pre-btn"
            data-active={active ? 'on' : 'off'}
            disabled={disabled}
            aria-pressed={active}
            // 再点一次取消：预选是可反悔的，不该只能靠「轮到自己」来解除
            onClick={() => onChange(active ? null : o.choice)}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export default PreActionBar
